import type React from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  getLucideComponent,
  getSimpleIconComponent,
  getLucideComponentAsync,
  getSimpleIconComponentAsync,
  getDoodleAsImage,
} from '../../lib/image-editor/iconRegistry';

interface CanvasStageProps {
  readonly imageUrl: string | null;
  /** 畫布背景色（當無 backgroundImageUrl 時使用） */
  readonly backgroundColor: string;
  /** 畫布背景圖（data URL），有值時以圖填滿預覽、覆蓋背景色 */
  readonly backgroundImageUrl?: string | null;
  readonly anchorX: number;
  readonly anchorY: number;
  /** 使用者設定的輸出畫布寬（px），0 表示未設定，預覽會依此比例顯示 */
  readonly outputCanvasWidth?: number;
  /** 使用者設定的輸出畫布高（px），0 表示未設定 */
  readonly outputCanvasHeight?: number;
  /** 畫布上圖片內容的邏輯尺寸（px），與 outputCanvas 同時存在時預覽會依比例顯示小圖＋留白 */
  readonly canvasContentWidth?: number;
  readonly canvasContentHeight?: number;
  /** 基礎調整（-100～100），用於畫布內圖片預覽 */
  readonly brightness?: number;
  readonly contrast?: number;
  readonly saturation?: number;
  /** 邊線處理預覽：形狀、圓角(px)、邊線粗細(px)、顏色 */
  readonly borderShape?: 'rect' | 'rounded' | 'circle';
  readonly borderCornerRadius?: number;
  readonly borderWidth?: number;
  readonly borderColor?: string;
  /** 圖示預覽：多個圖示（Lucide／品牌／塗鴉／上傳圖片），含 id、anchor、shape，可拖拉 */
  readonly iconItems?: readonly {
    id: string;
    source: 'lucide' | 'simple' | 'custom' | 'doodle';
    iconId?: string;
    imageDataUrl?: string;
    size: number;
    color: string;
    opacity: number;
    anchorX?: number;
    anchorY?: number;
    shape?: 'none' | 'circle' | 'square' | 'rounded';
  }[];
  onIconAnchorChange?(id: string, anchor: { x: number; y: number }): void;
  /** 雙擊預覽中圖示／塗鴉時呼叫，供開啟對應設定頁（id, source） */
  onIconDoubleClick?(
    id: string,
    source: 'lucide' | 'simple' | 'custom' | 'doodle'
  ): void;
  /** 浮水印預覽：多個文字塊，含 id 與 anchor 以支援拖拉定位 */
  readonly watermarkItems?: readonly {
    id: string;
    text: string;
    fontFamily: string;
    fontSize: number;
    color: string;
    opacity: number;
    anchorX?: number;
    anchorY?: number;
  }[];
  onChangeAnchor?(anchor: { x: number; y: number }): void;
  /** 單一文字塊錨點變更（拖拉後） */
  onWatermarkAnchorChange?(id: string, anchor: { x: number; y: number }): void;
  /** 點擊預覽中文字（未拖拉）時呼叫，供跳轉到該文字區塊 */
  onWatermarkClick?(id: string): void;
  /** QR Code 預覽：多個項目，每項含 id、尺寸、錨點；預覽圖由 qrCodePreviewUrls[id] 提供 */
  qrCodeItems?: readonly {
    id: string;
    content: string;
    size: number;
    anchorX: number;
    anchorY: number;
  }[];
  qrCodePreviewUrls?: Record<string, string>;
  onQrCodeAnchorChange?(id: string, anchor: { x: number; y: number }): void;
  /** 雙擊預覽中 QR Code 時呼叫，供開啟 QR Code 設定頁 */
  onQrCodeDoubleClick?(): void;
}

/**
 * 圖像舞台預覽：模擬最終輸出畫布，圖片可在其中拖曳移動位置。
 * 拖曳時以元件內 local state 即時更新位置，鬆開後再同步至父層。
 */
const STAGE_MAX = 360;

export function CanvasStage({
  imageUrl,
  backgroundColor,
  backgroundImageUrl = null,
  anchorX,
  anchorY,
  outputCanvasWidth = 0,
  outputCanvasHeight = 0,
  canvasContentWidth = 0,
  canvasContentHeight = 0,
  brightness = 0,
  contrast = 0,
  saturation = 0,
  borderShape = 'rect',
  borderCornerRadius = 0,
  borderWidth = 0,
  borderColor = '#000000',
  iconItems = [],
  onIconAnchorChange,
  onIconDoubleClick,
  watermarkItems = [],
  onChangeAnchor,
  onWatermarkAnchorChange,
  onWatermarkClick,
  qrCodeItems = [],
  qrCodePreviewUrls = {},
  onQrCodeAnchorChange,
  onQrCodeDoubleClick,
}: CanvasStageProps) {
  const filterStyle =
    brightness !== 0 || contrast !== 0 || saturation !== 0
      ? {
          filter: `brightness(${1 + brightness / 100}) contrast(${1 + contrast / 100}) saturate(${1 + saturation / 100})`,
        }
      : undefined;
  const hasBorder =
    borderShape !== 'rect' ||
    borderCornerRadius > 0 ||
    (borderWidth > 0 && !!borderColor);

  const hasOutputSize = outputCanvasWidth > 0 && outputCanvasHeight > 0;
  const hasContentSize = canvasContentWidth > 0 && canvasContentHeight > 0;
  const useCanvasContentRatio = hasOutputSize && hasContentSize;

  const aspectRatio = hasOutputSize
    ? outputCanvasWidth / outputCanvasHeight
    : 360 / 240;
  const stageWidth =
    aspectRatio >= 1 ? STAGE_MAX : Math.round(STAGE_MAX * aspectRatio);
  const stageHeight =
    aspectRatio >= 1 ? Math.round(STAGE_MAX / aspectRatio) : STAGE_MAX;

  /** 輸出畫布像素 → 預覽舞台像素，使 QR／邊線／圓角／文字與輸出比例一致 */
  const outputToStageScale =
    hasOutputSize && outputCanvasWidth > 0 && outputCanvasHeight > 0
      ? Math.min(
          stageWidth / outputCanvasWidth,
          stageHeight / outputCanvasHeight
        )
      : 1;

  const scaledBorderRadius = borderCornerRadius * outputToStageScale;
  const scaledBorderWidth = borderWidth * outputToStageScale;
  const borderStyle = hasBorder
    ? {
        borderRadius:
          borderShape === 'circle'
            ? '50%'
            : borderShape === 'rounded'
              ? `${scaledBorderRadius}px`
              : 0,
        border:
          borderWidth > 0 && borderColor
            ? `${scaledBorderWidth}px solid ${borderColor}`
            : undefined,
      }
    : undefined;

  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
    null
  );
  /** 拖曳時顯示用的錨點（0–1），鬆開後會同步到父層 */
  const [displayAnchorX, setDisplayAnchorX] = useState(anchorX);
  const [displayAnchorY, setDisplayAnchorY] = useState(anchorY);
  const [isDragging, setIsDragging] = useState(false);
  const [scale, setScale] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageInnerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{
    pointerX: number;
    pointerY: number;
    anchorX: number;
    anchorY: number;
  } | null>(null);
  const latestAnchorRef = useRef<{ x: number; y: number }>({
    x: displayAnchorX,
    y: displayAnchorY,
  });
  latestAnchorRef.current = { x: displayAnchorX, y: displayAnchorY };

  const [watermarkDrag, setWatermarkDrag] = useState<{
    id: string;
    anchorX: number;
    anchorY: number;
    startAnchorX: number;
    startAnchorY: number;
  } | null>(null);
  const watermarkCaptureTargetRef = useRef<HTMLElement | null>(null);

  const [qrCodeDrag, setQrCodeDrag] = useState<{
    id: string;
    anchorX: number;
    anchorY: number;
    startAnchorX: number;
    startAnchorY: number;
  } | null>(null);
  const qrCodeCaptureTargetRef = useRef<HTMLElement | null>(null);

  const [iconDrag, setIconDrag] = useState<{
    id: string;
    anchorX: number;
    anchorY: number;
    startAnchorX: number;
    startAnchorY: number;
  } | null>(null);
  const iconCaptureTargetRef = useRef<HTMLElement | null>(null);
  /** 從搜尋加入的圖示（介面未列出的）動態載入後快取 */
  const [loadedIcons, setLoadedIcons] = useState<
    Record<string, React.ComponentType<{ size?: number; color?: string }>>
  >({});
  const requestedIconsRef = useRef<Set<string>>(new Set());
  /** 塗鴉主色版 data URL（key: item.id + color），供預覽；無該 key 時用同 item 舊圖 fallback（樂觀 UI） */
  const [doodleSrcs, setDoodleSrcs] = useState<Record<string, string>>({});

  useEffect(() => {
    const doodlesWithColor = iconItems.filter(
      i =>
        i.source === 'doodle' && i.iconId && (i.color ?? '').trim().length > 0
    );
    let cancelled = false;
    doodlesWithColor.forEach(item => {
      const key = `${item.id}-${(item.color ?? '').trim()}`;
      getDoodleAsImage(item.iconId!, 120, (item.color ?? '').trim())
        .then(img => {
          if (!cancelled) setDoodleSrcs(prev => ({ ...prev, [key]: img.src }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [iconItems]);

  useEffect(() => {
    iconItems.forEach(item => {
      if (item.source === 'custom') return;
      const iconId = item.iconId ?? '';
      const key = `${item.source}:${iconId}`;
      if (requestedIconsRef.current.has(key)) return;
      const fromStatic =
        item.source === 'lucide'
          ? getLucideComponent(iconId)
          : getSimpleIconComponent(iconId);
      if (fromStatic) return;
      requestedIconsRef.current.add(key);
      (item.source === 'lucide'
        ? getLucideComponentAsync(iconId)
        : getSimpleIconComponentAsync(iconId)
      ).then(Icon => {
        if (Icon) setLoadedIcons(prev => ({ ...prev, [key]: Icon }));
      });
    });
  }, [iconItems]);

  const stageLayoutRef = useRef({
    stageWidth: 1,
    stageHeight: 1,
    scale: 0,
  });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !stageWidth || !stageHeight) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h <= 0) return;
      const s = Math.min(w / stageWidth, h / stageHeight);
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        setScale(s);
      }, 100);
    });
    observer.observe(el);
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (w > 0 && h > 0) setScale(Math.min(w / stageWidth, h / stageHeight));
    return () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [stageWidth, stageHeight]);

  useEffect(() => {
    if (!imageUrl) {
      setImageElement(null);
      return;
    }
    const image = new window.Image();
    image.onload = () => {
      setImageElement(image);
    };
    image.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (!isDragging) {
      setDisplayAnchorX(anchorX);
      setDisplayAnchorY(anchorY);
    }
  }, [anchorX, anchorY, isDragging]);

  const naturalWidth = imageElement?.naturalWidth ?? 0;
  const naturalHeight = imageElement?.naturalHeight ?? 0;

  let displayWidth: number;
  let displayHeight: number;

  if (useCanvasContentRatio && naturalWidth > 0 && naturalHeight > 0) {
    const contentBoxW = (stageWidth * canvasContentWidth) / outputCanvasWidth;
    const contentBoxH =
      (stageHeight * canvasContentHeight) / outputCanvasHeight;
    const scaleFit = Math.min(
      contentBoxW / naturalWidth,
      contentBoxH / naturalHeight
    );
    displayWidth = naturalWidth * scaleFit;
    displayHeight = naturalHeight * scaleFit;
  } else if (useCanvasContentRatio) {
    displayWidth = (stageWidth * canvasContentWidth) / outputCanvasWidth;
    displayHeight = (stageHeight * canvasContentHeight) / outputCanvasHeight;
  } else {
    const scaleFit =
      naturalWidth && naturalHeight
        ? Math.min(stageWidth / naturalWidth, stageHeight / naturalHeight)
        : 1;
    const fitsExactly =
      naturalWidth * scaleFit >= stageWidth - 2 &&
      naturalHeight * scaleFit >= stageHeight - 2;
    const scale = fitsExactly ? scaleFit * 0.92 : scaleFit;
    displayWidth = naturalWidth * scale;
    displayHeight = naturalHeight * scale;
  }

  const maxOffsetX = Math.max(stageWidth - displayWidth, 0);
  const maxOffsetY = Math.max(stageHeight - displayHeight, 0);

  const imageX = maxOffsetX * displayAnchorX;
  const imageY = maxOffsetY * displayAnchorY;
  stageLayoutRef.current = {
    stageWidth,
    stageHeight,
    scale,
  };

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!imageElement || (maxOffsetX === 0 && maxOffsetY === 0)) return;
      dragStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        anchorX: displayAnchorX,
        anchorY: displayAnchorY,
      };
      setIsDragging(true);
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [imageElement, maxOffsetX, maxOffsetY, displayAnchorX, displayAnchorY]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      const start = dragStartRef.current;
      if (!start || scale <= 0) return;
      const deltaScreenX = event.clientX - start.pointerX;
      const deltaScreenY = event.clientY - start.pointerY;
      const deltaX = deltaScreenX / scale;
      const deltaY = deltaScreenY / scale;
      const startX = maxOffsetX * start.anchorX;
      const startY = maxOffsetY * start.anchorY;
      const nextX = Math.min(Math.max(startX + deltaX, 0), maxOffsetX);
      const nextY = Math.min(Math.max(startY + deltaY, 0), maxOffsetY);
      const nextAnchorX = maxOffsetX > 0 ? nextX / maxOffsetX : 0.5;
      const nextAnchorY = maxOffsetY > 0 ? nextY / maxOffsetY : 0.5;
      setDisplayAnchorX(nextAnchorX);
      setDisplayAnchorY(nextAnchorY);
    },
    [maxOffsetX, maxOffsetY, scale]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!dragStartRef.current) return;
      const el = event.target as HTMLElement;
      if (el.hasPointerCapture && el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      if (onChangeAnchor) {
        onChangeAnchor({
          x: latestAnchorRef.current.x,
          y: latestAnchorRef.current.y,
        });
      }
      dragStartRef.current = null;
      setIsDragging(false);
    },
    [onChangeAnchor]
  );

  const visibleWatermarkItems = watermarkItems.filter(item =>
    item.text?.trim()
  );
  const showWatermark = visibleWatermarkItems.length > 0;

  const getWatermarkAnchor = useCallback(
    (item: (typeof watermarkItems)[number], _index: number) => {
      if (watermarkDrag?.id === item.id)
        return { x: watermarkDrag.anchorX, y: watermarkDrag.anchorY };
      return {
        x: item.anchorX ?? 0.5,
        y: item.anchorY ?? 0.5,
      };
    },
    [watermarkDrag]
  );

  const handleWatermarkPointerDown = useCallback(
    (
      event: React.PointerEvent,
      id: string,
      anchorX: number,
      anchorY: number
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const el = event.currentTarget as HTMLElement;
      watermarkCaptureTargetRef.current = el;
      setWatermarkDrag({
        id,
        anchorX,
        anchorY,
        startAnchorX: anchorX,
        startAnchorY: anchorY,
      });
      el.setPointerCapture(event.pointerId);
    },
    []
  );

  const handleWatermarkPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!watermarkDrag || !onWatermarkAnchorChange) return;
      const layout = stageLayoutRef.current;
      if (
        layout.scale <= 0 ||
        layout.stageWidth <= 0 ||
        layout.stageHeight <= 0 ||
        !stageInnerRef.current
      )
        return;
      const rect = stageInnerRef.current.getBoundingClientRect();
      const stageX = (event.clientX - rect.left) / layout.scale;
      const stageY = (event.clientY - rect.top) / layout.scale;
      const ax = Math.min(1, Math.max(0, stageX / layout.stageWidth));
      const ay = Math.min(1, Math.max(0, stageY / layout.stageHeight));
      setWatermarkDrag(prev =>
        prev ? { ...prev, anchorX: ax, anchorY: ay } : null
      );
    },
    [watermarkDrag, onWatermarkAnchorChange]
  );

  const handleWatermarkPointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!watermarkDrag) return;
      const el = watermarkCaptureTargetRef.current;
      if (el?.hasPointerCapture?.(event.pointerId))
        el.releasePointerCapture(event.pointerId);
      watermarkCaptureTargetRef.current = null;
      const moved =
        Math.abs(watermarkDrag.anchorX - watermarkDrag.startAnchorX) > 0.005 ||
        Math.abs(watermarkDrag.anchorY - watermarkDrag.startAnchorY) > 0.005;
      if (!moved) onWatermarkClick?.(watermarkDrag.id);
      onWatermarkAnchorChange?.(watermarkDrag.id, {
        x: watermarkDrag.anchorX,
        y: watermarkDrag.anchorY,
      });
      setWatermarkDrag(null);
    },
    [watermarkDrag, onWatermarkAnchorChange, onWatermarkClick]
  );

  const getQrCodeAnchor = useCallback(
    (item: { id: string; anchorX: number; anchorY: number }) => {
      if (qrCodeDrag?.id === item.id)
        return { x: qrCodeDrag.anchorX, y: qrCodeDrag.anchorY };
      return { x: item.anchorX, y: item.anchorY };
    },
    [qrCodeDrag]
  );

  const handleQrCodePointerDown = useCallback(
    (
      event: React.PointerEvent,
      item: { id: string; anchorX: number; anchorY: number }
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const el = event.currentTarget as HTMLElement;
      qrCodeCaptureTargetRef.current = el;
      setQrCodeDrag({
        id: item.id,
        anchorX: item.anchorX,
        anchorY: item.anchorY,
        startAnchorX: item.anchorX,
        startAnchorY: item.anchorY,
      });
      el.setPointerCapture(event.pointerId);
    },
    []
  );

  const handleQrCodePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!qrCodeDrag || !onQrCodeAnchorChange) return;
      const layout = stageLayoutRef.current;
      if (
        layout.scale <= 0 ||
        layout.stageWidth <= 0 ||
        layout.stageHeight <= 0 ||
        !stageInnerRef.current
      )
        return;
      const rect = stageInnerRef.current.getBoundingClientRect();
      const stageX = (event.clientX - rect.left) / layout.scale;
      const stageY = (event.clientY - rect.top) / layout.scale;
      const ax = Math.min(1, Math.max(0, stageX / layout.stageWidth));
      const ay = Math.min(1, Math.max(0, stageY / layout.stageHeight));
      setQrCodeDrag(prev =>
        prev ? { ...prev, anchorX: ax, anchorY: ay } : null
      );
    },
    [qrCodeDrag, onQrCodeAnchorChange]
  );

  const handleQrCodePointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!qrCodeDrag) return;
      const el = qrCodeCaptureTargetRef.current;
      if (el?.hasPointerCapture?.(event.pointerId))
        el.releasePointerCapture(event.pointerId);
      qrCodeCaptureTargetRef.current = null;
      onQrCodeAnchorChange?.(qrCodeDrag.id, {
        x: qrCodeDrag.anchorX,
        y: qrCodeDrag.anchorY,
      });
      setQrCodeDrag(null);
    },
    [qrCodeDrag, onQrCodeAnchorChange]
  );

  const getIconAnchor = useCallback(
    (item: (typeof iconItems)[number]) => {
      if (iconDrag?.id === item.id)
        return { x: iconDrag.anchorX, y: iconDrag.anchorY };
      return {
        x: item.anchorX ?? 0.5,
        y: item.anchorY ?? 0.5,
      };
    },
    [iconDrag]
  );

  const handleIconPointerDown = useCallback(
    (
      event: React.PointerEvent,
      id: string,
      anchorX: number,
      anchorY: number
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const el = event.currentTarget as HTMLElement;
      iconCaptureTargetRef.current = el;
      setIconDrag({
        id,
        anchorX,
        anchorY,
        startAnchorX: anchorX,
        startAnchorY: anchorY,
      });
      el.setPointerCapture(event.pointerId);
    },
    []
  );

  const handleIconPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!iconDrag || !onIconAnchorChange) return;
      const layout = stageLayoutRef.current;
      if (
        layout.scale <= 0 ||
        layout.stageWidth <= 0 ||
        layout.stageHeight <= 0 ||
        !stageInnerRef.current
      )
        return;
      const rect = stageInnerRef.current.getBoundingClientRect();
      const stageX = (event.clientX - rect.left) / layout.scale;
      const stageY = (event.clientY - rect.top) / layout.scale;
      const ax = Math.min(1, Math.max(0, stageX / layout.stageWidth));
      const ay = Math.min(1, Math.max(0, stageY / layout.stageHeight));
      setIconDrag(prev =>
        prev ? { ...prev, anchorX: ax, anchorY: ay } : null
      );
    },
    [iconDrag, onIconAnchorChange]
  );

  const handleIconPointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!iconDrag) return;
      const el = iconCaptureTargetRef.current;
      if (el?.hasPointerCapture?.(event.pointerId))
        el.releasePointerCapture(event.pointerId);
      iconCaptureTargetRef.current = null;
      onIconAnchorChange?.(iconDrag.id, {
        x: iconDrag.anchorX,
        y: iconDrag.anchorY,
      });
      setIconDrag(null);
    },
    [iconDrag, onIconAnchorChange]
  );

  return (
    <div className="rounded border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800">
      {imageUrl && imageElement ? (
        <div
          ref={containerRef}
          className="flex h-[420px] w-full items-center justify-center"
        >
          <div
            className="overflow-hidden rounded-sm"
            style={
              scale > 0
                ? {
                    width: stageWidth * scale,
                    height: stageHeight * scale,
                    maxWidth: '100%',
                  }
                : undefined
            }
          >
            <div
              ref={stageInnerRef}
              className="relative overflow-hidden select-none"
              style={{
                width: stageWidth,
                height: stageHeight,
                backgroundColor: backgroundImageUrl
                  ? undefined
                  : backgroundColor,
                touchAction: 'none',
                transform: `scale(${scale || 1})`,
                transformOrigin: 'top left',
              }}
              onPointerMove={
                watermarkDrag
                  ? handleWatermarkPointerMove
                  : iconDrag
                    ? handleIconPointerMove
                    : qrCodeDrag
                      ? handleQrCodePointerMove
                      : undefined
              }
              onPointerUp={
                watermarkDrag
                  ? handleWatermarkPointerUp
                  : iconDrag
                    ? handleIconPointerUp
                    : qrCodeDrag
                      ? handleQrCodePointerUp
                      : undefined
              }
              onPointerLeave={
                watermarkDrag
                  ? handleWatermarkPointerUp
                  : iconDrag
                    ? handleIconPointerUp
                    : qrCodeDrag
                      ? handleQrCodePointerUp
                      : undefined
              }
              onPointerCancel={
                watermarkDrag
                  ? handleWatermarkPointerUp
                  : iconDrag
                    ? handleIconPointerUp
                    : qrCodeDrag
                      ? handleQrCodePointerUp
                      : undefined
              }
            >
              {backgroundImageUrl && (
                <img
                  src={backgroundImageUrl}
                  alt=""
                  aria-hidden
                  className="pointer-events-none select-none object-cover"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: stageWidth,
                    height: stageHeight,
                    zIndex: 0,
                  }}
                />
              )}
              <img
                src={imageUrl ?? undefined}
                alt="輸出預覽"
                draggable={false}
                className="select-none"
                style={{
                  position: 'absolute',
                  zIndex: 0,
                  width: displayWidth,
                  height: displayHeight,
                  left: imageX,
                  top: imageY,
                  userSelect: 'none',
                  touchAction: 'none',
                  cursor:
                    maxOffsetX > 0 || maxOffsetY > 0
                      ? isDragging
                        ? 'grabbing'
                        : 'grab'
                      : 'default',
                  pointerEvents: 'auto',
                  ...filterStyle,
                  ...borderStyle,
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
              />
              {iconItems.length > 0 &&
                iconItems.map(item => {
                  const { x: ax, y: ay } = getIconAnchor(item);
                  const leftPx = ax * stageWidth;
                  const topPx = ay * stageHeight;
                  const scaledSize = item.size * outputToStageScale;
                  const iconSize = Math.max(16, scaledSize);
                  const iconKey = `${item.source}:${item.iconId ?? ''}`;
                  const IconComponent =
                    item.source === 'custom' || item.source === 'doodle'
                      ? null
                      : item.source === 'lucide'
                        ? (getLucideComponent(item.iconId ?? '') ??
                          loadedIcons[iconKey])
                        : (getSimpleIconComponent(item.iconId ?? '') ??
                          loadedIcons[iconKey]);
                  const isCustomImage =
                    item.source === 'custom' && item.imageDataUrl;
                  const isDoodleImage = item.source === 'doodle' && item.iconId;
                  const iconShape = item.shape ?? 'none';
                  const shapeStyle =
                    iconShape === 'circle'
                      ? {
                          borderRadius: '50%' as const,
                          overflow: 'hidden' as const,
                        }
                      : iconShape === 'rounded'
                        ? {
                            borderRadius: Math.max(4, iconSize * 0.2),
                            overflow: 'hidden' as const,
                          }
                        : { overflow: 'hidden' as const };
                  return (
                    <div
                      key={item.id}
                      role="img"
                      aria-label="圖示預覽（可拖拉、雙擊至設定）"
                      className="absolute z-10 cursor-grab select-none active:cursor-grabbing"
                      style={{
                        left: leftPx,
                        top: topPx,
                        width: iconSize,
                        height: iconSize,
                        transform: 'translate(-50%, -50%)',
                        opacity: item.opacity,
                        pointerEvents: 'auto',
                        touchAction: 'none',
                        ...shapeStyle,
                      }}
                      onPointerDown={e =>
                        handleIconPointerDown(e, item.id, ax, ay)
                      }
                      onPointerMove={handleIconPointerMove}
                      onPointerUp={handleIconPointerUp}
                      onPointerLeave={handleIconPointerUp}
                      onPointerCancel={handleIconPointerUp}
                      onDoubleClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onIconDoubleClick?.(item.id, item.source);
                      }}
                    >
                      {isCustomImage ? (
                        <img
                          src={item.imageDataUrl}
                          alt=""
                          className="h-full w-full object-contain"
                          width={iconSize}
                          height={iconSize}
                        />
                      ) : isDoodleImage ? (
                        (() => {
                          const colorTrim = (item.color ?? '').trim();
                          const exactKey = colorTrim
                            ? `${item.id}-${colorTrim}`
                            : '';
                          const src = colorTrim
                            ? (doodleSrcs[exactKey] ??
                              (() => {
                                const fallbackKey = Object.keys(
                                  doodleSrcs
                                ).find(k => k.startsWith(`${item.id}-`));
                                return fallbackKey
                                  ? doodleSrcs[fallbackKey]
                                  : item.iconId;
                              })())
                            : item.iconId;
                          if (
                            colorTrim &&
                            !doodleSrcs[exactKey] &&
                            !Object.keys(doodleSrcs).some(k =>
                              k.startsWith(`${item.id}-`)
                            )
                          ) {
                            return (
                              <div
                                aria-hidden
                                className="h-full w-full object-contain"
                                style={{
                                  width: iconSize,
                                  height: iconSize,
                                  visibility: 'hidden',
                                }}
                              />
                            );
                          }
                          return (
                            <img
                              src={src}
                              alt=""
                              className="h-full w-full object-contain"
                              width={iconSize}
                              height={iconSize}
                              crossOrigin={colorTrim ? undefined : 'anonymous'}
                            />
                          );
                        })()
                      ) : IconComponent ? (
                        <IconComponent size={iconSize} color={item.color} />
                      ) : null}
                    </div>
                  );
                })}
              {showWatermark &&
                visibleWatermarkItems.map((item, i) => {
                  const { x: ax, y: ay } = getWatermarkAnchor(item, i);
                  const leftPx = ax * stageWidth;
                  const topPx = ay * stageHeight;
                  const previewFontSize = Math.max(
                    10,
                    item.fontSize * outputToStageScale
                  );
                  return (
                    <div
                      key={item.id}
                      role="img"
                      aria-label="浮水印預覽（可拖拉）"
                      className="absolute z-10 cursor-grab select-none whitespace-nowrap py-1 px-2 active:cursor-grabbing"
                      style={{
                        left: leftPx,
                        top: topPx,
                        transform: 'translate(-50%, -50%)',
                        fontSize: previewFontSize,
                        fontFamily: item.fontFamily,
                        color: item.color,
                        opacity: item.opacity,
                        pointerEvents: 'auto',
                        touchAction: 'none',
                      }}
                      onPointerDown={e =>
                        handleWatermarkPointerDown(e, item.id, ax, ay)
                      }
                      onPointerMove={handleWatermarkPointerMove}
                      onPointerUp={handleWatermarkPointerUp}
                      onPointerLeave={handleWatermarkPointerUp}
                      onPointerCancel={handleWatermarkPointerUp}
                    >
                      {item.text.trim()}
                    </div>
                  );
                })}
              {qrCodeItems.map(item => {
                const previewUrl = qrCodePreviewUrls[item.id];
                if (!previewUrl) return null;
                const { x: ax, y: ay } = getQrCodeAnchor(item);
                const sizePx = Math.max(32, item.size * outputToStageScale);
                return (
                  <div
                    key={item.id}
                    role="img"
                    aria-label="QR Code 預覽（可拖拉、雙擊至設定）"
                    className="absolute z-10 cursor-grab active:cursor-grabbing"
                    style={{
                      left: ax * stageWidth,
                      top: ay * stageHeight,
                      width: sizePx,
                      height: sizePx,
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'auto',
                      touchAction: 'none',
                    }}
                    onPointerDown={e => handleQrCodePointerDown(e, item)}
                    onPointerMove={handleQrCodePointerMove}
                    onPointerUp={handleQrCodePointerUp}
                    onPointerLeave={handleQrCodePointerUp}
                    onPointerCancel={handleQrCodePointerUp}
                    onDoubleClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onQrCodeDoubleClick?.();
                    }}
                  >
                    <img
                      src={previewUrl}
                      alt=""
                      className="h-full w-full select-none object-contain"
                      draggable={false}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-48 items-center justify-center rounded border border-dashed border-gray-200 bg-gray-50/40 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400">
          尚未載入圖片。
        </div>
      )}
    </div>
  );
}
